class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.lockScroll();
    this.classList.add('animate');

    const drawerInner = this.querySelector('.drawer__inner');
    drawerInner?.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.classList.add('active');
    });

    if (drawerInner) {
      this.waitForDrawerTransition(drawerInner, () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = drawerInner || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      });
    }
  }

  close() {
    if (!this.classList.contains('active')) return;

    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    const drawerInner = this.querySelector('.drawer__inner');
    let finished = false;
    const finishClose = () => {
      if (finished) return;
      finished = true;
      this.classList.remove('animate');
      this.unlockScroll();
      if (drawerInner) drawerInner.style.willChange = '';
    };

    if (!drawerInner || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishClose();
      return;
    }

    this.waitForDrawerTransition(drawerInner, finishClose);
  }

  waitForDrawerTransition(drawerInner, callback) {
    let called = false;
    const onEnd = (event) => {
      // 子要素の transitionend がバブリングしてきても無視する
      if (event.target !== drawerInner) return;
      run();
    };
    const run = () => {
      if (called) return;
      called = true;
      clearTimeout(fallbackId);
      drawerInner.removeEventListener('transitionend', onEnd);
      callback();
    };

    const fallbackId = setTimeout(run, this.getDrawerTransitionMs(drawerInner) + 100);

    drawerInner.addEventListener('transitionend', onEnd);
  }

  getDrawerTransitionMs(drawerInner) {
    const style = getComputedStyle(drawerInner);
    const durations = style.transitionDuration
      .split(',')
      .map((value) => {
        const trimmed = value.trim();
        return trimmed.endsWith('ms') ? parseFloat(trimmed) : parseFloat(trimmed) * 1000;
      })
      .filter((value) => !Number.isNaN(value));

    return Math.max(...durations, 0) || 300;
  }

  lockScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--cart-drawer-scrollbar-width', `${scrollbarWidth}px`);
    document.body.classList.add('overflow-hidden');
  }

  unlockScroll() {
    document.body.classList.remove('overflow-hidden');
    document.documentElement.style.removeProperty('--cart-drawer-scrollbar-width');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
